import { useState } from "react";
import { Card, DataTable, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Interruptor, Modal } from "../../components/ui/primitivos";
import {
  actualizarItemCatalogo,
  borrarItemCatalogo,
  CATALOGOS,
  type CatalogoItem,
  crearItemCatalogo,
  type DefinicionCatalogo,
  fetchCatalogo,
  fetchEstadosStock,
  reordenarCatalogo,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";

/**
 * Parámetros del sistema — porteada de `CatalogsPage` de `imprenta`.
 *
 * Una pestaña por catálogo, con el switch de "mostrar inactivos" como allá. Los
 * cinco comparten formulario porque comparten forma: nombre, activo, y los
 * campos propios que declara cada definición.
 *
 * **"Eliminar" desactiva, no borra** — igual que el endpoint. El botón dice
 * "Desactivar" justamente para que nadie espere otra cosa: `imprenta` lo llama
 * eliminar y desactiva, y esa diferencia entre lo que dice y lo que hace es la
 * que asusta a quien lo aprieta.
 */
export function CatalogosPagina() {
  const { can } = useSession();
  const [activa, setActiva] = useState<DefinicionCatalogo>(CATALOGOS[0] as DefinicionCatalogo);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [editando, setEditando] = useState<CatalogoItem | null>(null);
  const [creando, setCreando] = useState(false);
  // Los estados de stock no son un `DefinicionCatalogo`: no se listan, no se
  // crean y no se ordenan. Van en su propio interruptor en vez de forzarlos
  // dentro de `activa`.
  const [enEstadosStock, setEnEstadosStock] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = useDatos(
    () => fetchCatalogo(activa.ruta, mostrarInactivos),
    `${activa.ruta}:${mostrarInactivos}`,
  );

  const puedeCrear = can("catalogos", "create");
  const puedeEditar = can("catalogos", "update");
  const puedeBorrar = can("catalogos", "delete");

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await items.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  /** Mueve un item una posición, y manda las DOS filas afectadas juntas. */
  async function mover(indice: number, direccion: -1 | 1) {
    const lista = items.datos ?? [];
    const actual = lista[indice];
    const vecino = lista[indice + direccion];
    // `orden` solo existe en los catalogos ordenables; sin el no hay nada que
    // intercambiar.
    if (!actual || !vecino || actual.orden === undefined || vecino.orden === undefined) return;
    const ordenActual = actual.orden;
    const ordenVecino = vecino.orden;
    await accion(() =>
      reordenarCatalogo(activa.ruta, [
        { id: actual.id, orden: ordenVecino },
        { id: vecino.id, orden: ordenActual },
      ]),
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Parámetros"
        subtitulo="Catálogos que alimentan los selectores del sistema"
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {items.error ? <Aviso>{items.error}</Aviso> : null}

      <nav className="flex flex-wrap gap-1">
        {CATALOGOS.map((cat) => (
          <button
            key={cat.ruta}
            type="button"
            onClick={() => {
              setEnEstadosStock(false);
              setActiva(cat);
            }}
            className={pestana(!enEstadosStock && cat.ruta === activa.ruta)}
          >
            {cat.titulo}
          </button>
        ))}
        {/* Los estados de stock son una pestaña más en `imprenta`, y de solo
            lectura: son los valores del enum, no filas de una tabla. */}
        <button
          type="button"
          onClick={() => setEnEstadosStock(true)}
          className={pestana(enEstadosStock)}
        >
          Estados de stock
        </button>
      </nav>

      {enEstadosStock ? <EstadosDeStock /> : null}

      {enEstadosStock ? null : (
        <Card className="space-y-3 p-4">
          {/* La tarjeta con su titulo, el toggle y «+ Nuevo» adentro: es la
            disposicion de `CatalogsPage`, y el titulo dice de qué catálogo son
            las filas sin tener que mirar cuál pestaña está activa. */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-semibold text-neutral-900 text-sm">{activa.titulo}</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-neutral-600 text-sm">
                <Interruptor
                  aria-label="Mostrar inactivos"
                  checked={mostrarInactivos}
                  onCheckedChange={setMostrarInactivos}
                />
                <span aria-hidden="true">Mostrar inactivos</span>
              </div>
              {puedeCrear ? <Boton onClick={() => setCreando(true)}>+ Nuevo</Boton> : null}
            </div>
          </div>

          <DataTable
            datos={items.datos ?? []}
            clave={(i) => i.id}
            cargando={items.cargando}
            mensajeVacio={`No hay items en ${activa.titulo.toLowerCase()}`}
            columnas={[
              {
                clave: "nombre",
                encabezado: "Nombre",
                className: "font-medium",
                render: (item) => (
                  // Los inactivos se atenúan pero NO se ocultan: el borrado es
                  // lógico y hay que poder reactivarlos.
                  <span className={item.activo ? "" : "opacity-60"}>
                    {activa.ordenable ? (
                      <span className="mr-2 text-neutral-400 text-xs">{item.orden}</span>
                    ) : null}
                    {item.nombre}
                  </span>
                ),
              },
              {
                clave: "detalle",
                encabezado: "Detalle",
                className: "text-neutral-600",
                render: (item) => activa.detalle(item),
              },
              {
                clave: "estado",
                encabezado: "Estado",
                render: (item) => (
                  <Etiqueta tono={item.activo ? "ok" : "neutral"}>
                    {item.activo ? "Activo" : "Inactivo"}
                  </Etiqueta>
                ),
              },
              {
                clave: "acciones",
                encabezado: "Acciones",
                className: "text-right",
                render: (item, indice) => (
                  <div className="flex justify-end gap-1">
                    {activa.ordenable && puedeEditar ? (
                      <>
                        <Boton
                          variante="fantasma"
                          aria-label={`Subir ${item.nombre}`}
                          disabled={indice === 0}
                          onClick={() => mover(indice, -1)}
                        >
                          ↑
                        </Boton>
                        <Boton
                          variante="fantasma"
                          aria-label={`Bajar ${item.nombre}`}
                          disabled={indice === (items.datos?.length ?? 0) - 1}
                          onClick={() => mover(indice, 1)}
                        >
                          ↓
                        </Boton>
                      </>
                    ) : null}
                    {puedeEditar ? (
                      <Boton variante="fantasma" onClick={() => setEditando(item)}>
                        Editar
                      </Boton>
                    ) : null}
                    {puedeBorrar && item.activo ? (
                      <Boton
                        variante="peligro"
                        onClick={() => accion(() => borrarItemCatalogo(activa.ruta, item.id))}
                      >
                        Desactivar
                      </Boton>
                    ) : null}
                    {puedeEditar && !item.activo ? (
                      <Boton
                        variante="secundario"
                        onClick={() =>
                          accion(() =>
                            actualizarItemCatalogo(activa.ruta, item.id, { activo: true }),
                          )
                        }
                      >
                        Activar
                      </Boton>
                    ) : null}
                  </div>
                ),
              },
            ]}
          />
        </Card>
      )}

      {editando || creando ? (
        <ModalItem
          definicion={activa}
          item={editando}
          onCerrar={() => {
            setEditando(null);
            setCreando(false);
          }}
          onGuardado={async () => {
            setEditando(null);
            setCreando(false);
            await items.recargar();
          }}
        />
      ) : null}
    </div>
  );
}

/** El estilo de una pestaña, activa o no. */
function pestana(activa: boolean): string {
  return `rounded-md px-3 py-1.5 text-sm ${
    activa ? "bg-neutral-200 text-neutral-900" : "text-neutral-600 hover:text-neutral-800"
  }`;
}

/**
 * Los estados de stock, en tarjetas de solo lectura — el bloque de
 * `CatalogsPage` (ADR 0071).
 *
 * Son los valores del enum: no se crean, no se editan y no se desactivan.
 * Mostrarlos igual sirve para saber qué significa cada uno sin abrir el código.
 */
function EstadosDeStock() {
  const estados = useDatos(fetchEstadosStock);

  return (
    <Card className="space-y-3 p-4">
      <div>
        <h2 className="font-semibold text-neutral-900 text-sm">Estados de stock</h2>
        <p className="text-neutral-500 text-sm">Estados predefinidos del sistema (solo lectura)</p>
      </div>

      {estados.error ? <Aviso>{estados.error}</Aviso> : null}

      <div className="grid gap-3 md:grid-cols-3">
        {(estados.datos ?? []).map((estado) => (
          <div key={estado.value} className="rounded-lg border border-neutral-200 p-3">
            <p className="flex items-center gap-2">
              <Etiqueta>{estado.value}</Etiqueta>
              <span className="font-medium text-neutral-900">{estado.label}</span>
            </p>
            <p className="mt-2 text-neutral-600 text-sm">{estado.descripcion}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ModalItem({
  definicion,
  item,
  onCerrar,
  onGuardado,
}: {
  definicion: DefinicionCatalogo;
  item: CatalogoItem | null;
  onCerrar: () => void;
  onGuardado: () => Promise<void>;
}) {
  const [valores, setValores] = useState<Record<string, string>>(() => {
    const inicial: Record<string, string> = { nombre: item?.nombre ?? "" };
    for (const campo of definicion.campos) {
      const valor = item?.[campo.clave];
      inicial[campo.clave] = valor === null || valor === undefined ? "" : String(valor);
    }
    return inicial;
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      const cuerpo: Record<string, unknown> = { nombre: valores.nombre?.trim() ?? "" };
      for (const campo of definicion.campos) {
        const crudo = valores[campo.clave]?.trim() ?? "";
        if (campo.tipo === "numero") cuerpo[campo.clave] = crudo === "" ? null : Number(crudo);
        else if (campo.tipo === "booleano") cuerpo[campo.clave] = crudo === "si";
        else cuerpo[campo.clave] = crudo === "" ? null : crudo;
      }
      if (item) await actualizarItemCatalogo(definicion.ruta, item.id, cuerpo);
      else await crearItemCatalogo(definicion.ruta, cuerpo);
      await onGuardado();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={item ? item.nombre : `Nuevo en ${definicion.titulo.toLowerCase()}`}
    >
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}

        <Campo
          etiqueta="Nombre"
          value={valores.nombre ?? ""}
          onChange={(e) => setValores((v) => ({ ...v, nombre: e.target.value }))}
        />

        {definicion.campos.map((campo) =>
          campo.tipo === "booleano" ? (
            <div key={campo.clave} className="flex items-center gap-2 text-neutral-700 text-sm">
              <Interruptor
                aria-label={campo.etiqueta}
                checked={valores[campo.clave] === "si"}
                onCheckedChange={(v) =>
                  setValores((prev) => ({ ...prev, [campo.clave]: v ? "si" : "no" }))
                }
              />
              <span aria-hidden="true">{campo.etiqueta}</span>
            </div>
          ) : (
            <Campo
              key={campo.clave}
              etiqueta={campo.etiqueta}
              type={campo.tipo === "numero" ? "number" : "text"}
              value={valores[campo.clave] ?? ""}
              onChange={(e) => setValores((v) => ({ ...v, [campo.clave]: e.target.value }))}
            />
          ),
        )}

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton onClick={guardar} disabled={guardando || (valores.nombre ?? "").trim() === ""}>
            {guardando ? "Guardando..." : item ? "Guardar" : "Crear"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
